// src/com/veh_info/VehInfoOwned.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import SearchBox from '../../common/SearchBox';
import {
    HoriContentContainer, ContentConainer, ContentHeader, ContentBody, ControlBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle } from '../../value/AppConst';
import {
    vehInfoListOwnedRepo, comInfoListWithOwnVehCountRepo, comInfoSelectedWithOwnVehCountRepo
} from '../../value/AppModel';
import { LIST_VEH_INFO_OF_COMPANY } from './VehInfoGql';
import { LIST_COM_INFO_WITH_OWN_VEH_COUNT } from '../com_info/ComInfoGql';
import { ComInfoDictionary } from '../com_info/ComInfoDict';
import { VehInfoDictionary } from './VehInfoDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import ValueMisc from '../../value/ValueMisc';
import { CircularProgress } from '@mui/material';

const ColumnBox = styled(Box)({display:'flex', alignItems:'center', height:'100%'});
const NameBox = styled(Box)({flexGrow:1, display:'flex', alignItems:'center'});
const GuideLabel = styled(Box)({
    borderRadius:20, border: AppStyle.BorderCCC, padding:'5px 15px', });
const RoundLabel = styled(Box)({
    borderRadius:20, backgroundColor:AppStyle.PrimaryRGB, color:'white', padding:'5px 15px', fontWeight:'bold', marginRight:5
});

const ComTableProps = ComInfoDictionary.comListWithOwnedVehCountProps;

/**
 * (VEH_INFO) 정보 View Controller
 * 운송사별 차량 목록. (운송사별 고정차)
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-07 12:55:53 using DbdbTap
 */
export default function VehInfoOwned({
    viewServiceTags, // for switching view
    show = true,
}) {
    const comInfoListWithOwnVehCountData = useReactiveVar(comInfoListWithOwnVehCountRepo);
    const vehInfoListData = useReactiveVar(vehInfoListOwnedRepo);
    const comSelected = useReactiveVar(comInfoSelectedWithOwnVehCountRepo);
    const [searchComText, setSearchComText] = useState('');
    const [searchVehText, setSearchVehText] = useState('');
    const addAlert = useAlertSetter();

    // ##### Call GraphQL to get List #####
    const [getComInfoListWithOwnVehCount, responseComInfoList] = useLazyQuery(LIST_COM_INFO_WITH_OWN_VEH_COUNT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComInfoList(data, option)},
        onError: (error) => addAlert({title: '운송사 목록 조회 오류', message: [error.message]})
    });

    const [getVehInfoList, responseList] = useLazyQuery(LIST_VEH_INFO_OF_COMPANY, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehInfoDictionary.errorTitle.Get, message: [error.message]})
    });

    useEffect(()=>{
        const diff = new Date().getTime()/1000 - comInfoListWithOwnVehCountData.when;
        if(diff > 300)
            getComInfoListWithOwnVehCount();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetComInfoList = (data, clientOption) => {
        if(data.comInfoListWithOwnVehCount) {
            comInfoListWithOwnVehCountRepo({when:new Date().getTime()/1000, records:data.comInfoListWithOwnVehCount});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.vehInfoListOfCompany) {
            vehInfoListOwnedRepo({when:new Date().getTime()/1000, records:data.vehInfoListOfCompany});
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onChangeSearchComText = (text) => {
        setSearchComText(text);
    }

    const onChangeSearchVehText = (text) => {
        setSearchVehText(text);
    };

    const onClickComInfo = (comInfo) => {
        comInfoSelectedWithOwnVehCountRepo(comInfo);
        getVehInfoList({variables:{comId:comInfo.comId, rentYn:'N'}});
    };

    const loadingAny
    = responseComInfoList.loading
    || responseList.loading;

    const renderVehListTitle = () => {
        if(comSelected.comId) {
            return(
                <NameBox>
                    <RoundLabel>{comSelected.comName}</RoundLabel>
                    <RoundLabel>{comSelected.dcName}</RoundLabel>
                    <RoundLabel>{ValueMisc.int2str(comSelected.countVehicle)}대</RoundLabel>
                </NameBox>
            );
        }
        else {
            return(
                <NameBox><GuideLabel>운송사를 선택하세요</GuideLabel></NameBox>
            );
        }
    };

    const rootStyle = show ? undefined : {display:'none', visibility:'hidden', pointerEvents:'none'};

    return(
        <ContentConainer style={rootStyle}>
            {viewServiceTags}
            <ContentBody>
                <HoriContentContainer>
                    <ColumnBox width={580}>
                        <ContentConainer>
                            <ContentHeader>
                                <ControlBox>
                                    <SearchBox
                                        value={searchComText}
                                        onChangeText={onChangeSearchComText}
                                    />
                                </ControlBox>
                            </ContentHeader>
                            <FlexyTable
                                name={ComTableProps.tableId}
                                tableProps={ComTableProps}
                                records={comInfoListWithOwnVehCountData.records}
                                search={searchComText}
                                onRowClicked={onClickComInfo}
                            />
                        </ContentConainer>
                    </ColumnBox>
                    <ColumnBox style={{flexGrow:1}}>
                        <ContentConainer>
                            <ContentHeader>
                                <ControlBox>
                                    {renderVehListTitle()}
                                    {
                                        loadingAny &&
                                        <CircularProgress size={20} />
                                    }
                                    <SearchBox
                                        value={searchVehText}
                                        onChangeText={onChangeSearchVehText}
                                    />
                                </ControlBox>
                            </ContentHeader>
                            <FlexyTable
                                name={VehInfoDictionary.tableProps.tableId}
                                tableProps={VehInfoDictionary.tableProps}
                                records={vehInfoListData.records}
                                search={searchVehText}
                            />
                        </ContentConainer>
                    </ColumnBox>
                </HoriContentContainer>
            </ContentBody>
        </ContentConainer>
    );
}
